import {
  memo, useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { MapContext } from '@react-google-maps/api';
import {
  MarkerClusterer as GoogleClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer';

import IbePropTypes from '../../IbePropTypes';
import createClusterMarker from '../../lib/createCluster';
import theme from '../../styles/config/theme';
import { CLUSTERER_DEFAULT_OPTIONS } from './constants';
import { toInt } from '../Search/ClusteringPanel';

const CLUSTER_MARKER_SIZE = 42;

const MarkerClusterer = ({
  children,
  gridSize,
  minimumClusterSize,
  onLoad,
}) => {
  const map = useContext(MapContext);
  const clusterer = useRef(null);

  const [markers, setMarkers] = useState({});

  // Ref holds last applied list of markers
  const prevMarkers = useRef(new Set());

  useEffect(function setupClusterer() {
    if (!map) return null;

    const algorithm = new SuperClusterAlgorithm({
      radius: toInt(gridSize, CLUSTERER_DEFAULT_OPTIONS.gridSize),
      minPoints: toInt(
        minimumClusterSize,
        CLUSTERER_DEFAULT_OPTIONS.minClusterSize,
      ),
      nodeSize: CLUSTERER_DEFAULT_OPTIONS.nodeSize,
    });

    clusterer.current = new GoogleClusterer({
      map,
      markers: [],
      algorithm,
      renderer: {
        render: ({
          count,
          _position: position,
        }) => new window.google.maps.marker.AdvancedMarkerElement({
          position,
          content: createClusterMarker(
            CLUSTER_MARKER_SIZE,
            theme.COLOR_LAKE_GREEN_DARK,
            count,
          ).documentElement,
        }),
      },
    });

    onLoad(clusterer.current);

    return () => {
      clusterer.current.clearMarkers();
      clusterer.current = null;
      prevMarkers.current = new Set();
      setMarkers({});
    };
  }, [map, gridSize, minimumClusterSize]);

  // Sync actual clusterer markers to state
  useEffect(function onMarkersUpdate() {
    if (!clusterer.current) return;

    const currentMarkers = prevMarkers.current;
    const nextMarkers = new Set(Object.values(markers));

    const toAdd = [...nextMarkers].filter((m) => !currentMarkers.has(m));
    const toRemove = [...currentMarkers].filter((m) => !nextMarkers.has(m));

    if (toRemove.length > 0) {
      clusterer.current.removeMarkers(toRemove);
      toRemove.forEach((m) => currentMarkers.delete(m));
    }

    if (toAdd.length > 0) {
      clusterer.current.addMarkers(toAdd);
      toAdd.forEach((m) => currentMarkers.add(m));
    }
  }, [markers]);

  // Add or remove marker from state (and allow external components to call these)
  const addMarker = (marker, key) => {
    setMarkers((prev) => {
      if (marker && !prev[key]) {
        return { ...prev, [key]: marker };
      }
      return prev;
    });
  };

  const removeMarker = (key) => {
    setMarkers((prev) => {
      if (!prev[key]) return prev;
      const newMarkers = { ...prev };
      delete newMarkers[key];
      return newMarkers;
    });
  };

  return children({
    clusterer: clusterer.current,
    addMarker,
    removeMarker,
  });
};

MarkerClusterer.propTypes = {
  children: IbePropTypes.children.isRequired,
  gridSize: PropTypes.number,
  minimumClusterSize: PropTypes.number,
  onLoad: PropTypes.func,
};

MarkerClusterer.defaultProps = {
  gridSize: CLUSTERER_DEFAULT_OPTIONS.gridSize,
  minimumClusterSize: CLUSTERER_DEFAULT_OPTIONS.minClusterSize,
  onLoad: () => {},
};

export default memo(MarkerClusterer);
